import { useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import { ERROR_404 } from 'common/config/api';
import {
  COMPANIES_WITH_OWNERSHIP_BY_FUND_DEFAULT_ERROR_MESSAGE,
  COMPANIES_WITH_OWNERSHIP_BY_FUND_QUERY_KEY,
  SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/funds';
import { SuperAgentError } from 'common/types/services';
import { FundService } from 'services';
import { throwError } from 'utilities';
import {
  GetCompaniesWithOwnershipByFundParams,
  GetCompaniesWithOwnershipByFundResponse,
  GetCompaniesWithOwnershipByFundReturn,
  UseGetCompaniesWithOwnershipByFundParams,
} from './useGetCompaniesWithOwnershipByFund.types';

const getCompaniesWithOwnershipByFund = async (
  params: GetCompaniesWithOwnershipByFundParams
): Promise<GetCompaniesWithOwnershipByFundResponse | null> => {
  const { fundId } = params;

  if (isNil(fundId)) {
    return null;
  }

  const fundService = new FundService();

  try {
    const companiesWithOwnership = (await fundService.getCompaniesWithOwnershipByFund(
      fundId
    )) as GetCompaniesWithOwnershipByFundResponse;

    if (!companiesWithOwnership) {
      return throwError(COMPANIES_WITH_OWNERSHIP_BY_FUND_DEFAULT_ERROR_MESSAGE);
    }

    return companiesWithOwnership;
  } catch (error) {
    // Handle Companies not found for this fund
    if (error instanceof Error) {
      const { response } = error as SuperAgentError;

      if (toString(response?.error?.status) === ERROR_404) {
        return [] as GetCompaniesWithOwnershipByFundResponse;
      }
    }

    return throwError(COMPANIES_WITH_OWNERSHIP_BY_FUND_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetCompaniesWithOwnershipByFund = (
  params: UseGetCompaniesWithOwnershipByFundParams
): GetCompaniesWithOwnershipByFundReturn => {
  const { fund, shouldQueryAutomatically = true } = params;

  const fundId = fund?.id;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<
    GetCompaniesWithOwnershipByFundReturn['data'],
    Error
  >({
    enabled: !isNil(fundId) && shouldQueryAutomatically,
    queryFn: () => getCompaniesWithOwnershipByFund({ fundId }),
    queryKey: [COMPANIES_WITH_OWNERSHIP_BY_FUND_QUERY_KEY, fundId],
    refetchOnWindowFocus: SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetCompaniesWithOwnershipByFund;
